import React, {lazy, Suspense, useEffect} from 'react';
import {ActivityIndicator, Platform, View} from 'react-native';
import {createStackNavigator} from '@react-navigation/stack';

import {observer} from 'mobx-react';

import {NavigationKey} from '../config/navigation.config';
import useStores from '../../stores/useStores';

import AuthStackNavigator from './AuthStackNavigator';
import AdminDeleteAccountScreen from '../../screens/admin/AdminDeleteAccountScreen';
import PrivacyPolicyScreen from '../../screens/policy/PrivacyPolicyScreen';
import SharedDoctorProfileScreen from '../../screens/consultation/SharedDoctorProfileScreen';
import DoctorProfileStoreRedirectScreen from '../../screens/consultation/DoctorProfileStoreRedirectScreen';
import {consumePendingSharedPractitionerId} from '../../utils/pending-share.utils';
import {navigateToRootScreen} from '../../utils/nav.utils';

const OnboardingStackNavigator = lazy(() => import('./OnboardingStackNavigator'));
const AdminDrawerNavigator = lazy(() => import('./AdminDrawerNavigator'));
const PatientDrawerNavigator = lazy(() => import('./PatientDrawerNavigator'));
const DoctorDrawerNavigator = lazy(() => import('./DoctorDrawerNavigator'));
const PaymentWebViewStackNavigator = lazy(
  () => import('./PaymentWebViewStackNavigator'),
);

const {Navigator, Screen} = createStackNavigator();

const RootStackNavigator = () => {
  const {authStore} = useStores();

  // Resume a doctor share link that was tapped while logged out: once a
  // patient session becomes active, SHARED_DOCTOR_PROFILE exists in the tree
  // (see the patient branch below) - navigate there if a link is pending.
  // The short delay gives the newly-mounted patient screens a moment to
  // register with the navigator, matching this app's existing pattern for
  // navigating right after a conditionally-rendered screen appears (see
  // LoginScreen's validateRegistrationStatus).
  useEffect(() => {
    if (authStore.loggedIn && authStore.userRole === 'patient') {
      const practitionerId = consumePendingSharedPractitionerId();
      if (practitionerId) {
        const timeout = setTimeout(() => {
          navigateToRootScreen(NavigationKey.SHARED_DOCTOR_PROFILE, {practitionerId});
        }, 300);
        return () => clearTimeout(timeout);
      }
    }
  }, [authStore.loggedIn, authStore.userRole]);

  const getOnboardingScreen = () => {
    return (
      <Screen
        name={NavigationKey.ONBOARDING_STACK}
        component={OnboardingStackNavigator}
      />
    );
  };

  const getAuthScreen = () => {
    return (
      <>
        <Screen
          name={NavigationKey.AUTH_STACK}
          component={AuthStackNavigator}
        />
        <Screen
          name={NavigationKey.PAYMENT_WEB_VIEW_STACK}
          component={PaymentWebViewStackNavigator}
        />
        <Screen
          name={NavigationKey.ADMIN_DELETE_ACCOUNT_SCREEN}
          component={AdminDeleteAccountScreen}
        />
      </>
    );
  };

  const getMainScreen = () => {
    switch (authStore.auth?.user.userRole) {
      case 'admin':
        return (
          <Screen
            name={NavigationKey.MAIN_DRAWER}
            component={AdminDrawerNavigator}
          />
        );

      case 'patient':
        return (
          <>
            <Screen
              name={NavigationKey.MAIN_DRAWER}
              component={PatientDrawerNavigator}
            />
            <Screen
              name={NavigationKey.PAYMENT_WEB_VIEW_STACK}
              component={PaymentWebViewStackNavigator}
            />
            <Screen
              name={NavigationKey.SHARED_DOCTOR_PROFILE}
              component={SharedDoctorProfileScreen}
            />
          </>
        );

      case 'practitioner':
        return (
          <Screen
            name={NavigationKey.MAIN_DRAWER}
            component={DoctorDrawerNavigator}
          />
        );

      default:
        return null;
    }
  };

  const getNavigator = (children: React.ReactNode) => {
    return (
      <Suspense
        fallback={
          <View style={{flex: 1, alignItems: 'center', justifyContent: 'center'}}>
            <ActivityIndicator />
          </View>
        }>
        <Navigator
          screenOptions={{
            headerShown: false,
          }}>
          {children}
          <Screen
            name={NavigationKey.PRIVACY_POLICY}
            component={PrivacyPolicyScreen}
          />
          {/* Always mounted (regardless of auth state) so a doctor's share
              link resolves to a store redirect on the web build even when
              no session is active. Native ignores this route entirely - see
              NavigationWebLinkingConfig vs NavigationLinkingConfig. */}
          <Screen
            name={NavigationKey.DOCTOR_PROFILE_STORE_REDIRECT}
            component={DoctorProfileStoreRedirectScreen}
          />
        </Navigator>
      </Suspense>
    );
  };

  const getAuthStateScreen = () => {
    return authStore.loggedIn ? getMainScreen() : getOnboardingStateScreen();
  };

  const getOnboardingStateScreen = () => {
    if (Platform.OS === 'web') {
      return getAuthScreen();
    } else {
      return authStore.isOnboardingDone
        ? getAuthScreen()
        : getOnboardingScreen();
    }
  };

  return getNavigator(getAuthStateScreen());
};

export default observer(RootStackNavigator);
